import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Select } from '@ngxs/store';
import { UserModel } from '@app/modules/user/model/user';
import { UserState } from '@app/modules/user/store/user.state';

interface ClinicianNavItem {
  label: string;
  icon: string;
  link: string;
  /** Extra prefixes that should also light this item up (e.g. redirects). */
  activePrefixes?: string[];
  exact?: boolean;
}

/**
 * Persistent left navigation for DOCTOR users across /office — mirrors the
 * patient sidebar. Patients, admins and proxies never see it (template renders
 * nothing for them), so their existing navigation is untouched. Collapses to a
 * horizontal strip on small screens. Links point at EXISTING routes only; the
 * Epic and Verify entries live outside /office (their own full-page layouts).
 */
@Component({
  selector: 'app-doctor-sidebar',
  templateUrl: './doctor-sidebar.component.html',
  styleUrls: ['./doctor-sidebar.component.sass'],
})
export class DoctorSidebarComponent {
  @Select(UserState.model) public user$: Observable<UserModel>;

  /**
   * THE clinician navigation — every doctor destination has exactly one home
   * here. The top header deliberately carries no doctor nav links anymore
   * (only the scan-QR quick action, notifications and the account menu), so
   * this list must stay complete: a destination missing from it is a
   * destination with no navigation at all.
   */
  public readonly items: ClinicianNavItem[] = [
    { label: 'Dashboard', icon: 'pi-home', link: '/office/dashboard/doctor', exact: true },
    // Three different sets of people, three destinations. They used to share
    // one page, which is why "My patients" and the org's active orders showed
    // the same list with a filter and nobody could find who had shared.
    {
      label: 'My patients',
      icon: 'pi-users',
      link: '/office/patients',
      activePrefixes: ['/office/patients'],
    },
    {
      label: 'Shared with me',
      icon: 'pi-share-alt',
      link: '/office/shared',
      activePrefixes: ['/office/shared'],
    },
    {
      label: 'MOLST orders',
      icon: 'pi-file',
      link: '/office/orders',
      activePrefixes: ['/office/orders'],
    },
    {
      label: 'Epic Patient Search',
      icon: 'pi-search',
      link: '/epic/patients',
      activePrefixes: ['/epic/patients'],
    },
    // Clinical Records / Add Clinical Record were retired (team decision
    // 2026-08-04): general record exchange (MRI, trials, reports) is the
    // EHR's job — this product is directives, medical orders and consent.
    {
      label: 'Track & Verify Consent',
      icon: 'pi-check-circle',
      link: '/office/consent/track',
      activePrefixes: ['/office/consent/track'],
    },
    {
      label: 'Verify / Scan QR',
      icon: 'pi-qrcode',
      link: '/public-molst-lookup',
      activePrefixes: ['/public-molst-lookup'],
    },
    // Security lives in the account dropdown ("Security & two-factor") —
    // it's an account concern, not a clinical destination.
  ];

  constructor(private readonly router: Router) {}

  public isActive(item: ClinicianNavItem): boolean {
    const url = this.router.url.split('?')[0];
    if (item.exact) {
      return url === item.link;
    }
    return (item.activePrefixes || [item.link]).some((prefix) => url === prefix || url.startsWith(`${prefix}/`));
  }
}
